/**
 * Represents a single point of a Metric.
 */
export interface IMetricDataPoint {
  // The measure value of the Metric at this point.
  measure?: number;

  // The timestamp of the data point. This may or may not include a timezone.
  // If no timezone is provided, this should be considered to have occurred in the user's local timezone.
  // This is an ISO8601 string.
  timestamp?: string;
}

/**
 * Represents the range of values for ordinal data.
 */
export interface IMetricDataPointsRange<T> {
  // The lowest value being returned.
  min: T;
  // The largest value being returned.
  max: T;
}

/**
 * This represents the lowest possible granularity of the data.
 */
export enum DateGranularity {
  UNKNOWN = 'unknown',
  SECOND = 'second',
  MINUTE = 'minute',
  HOUR = 'hour',
  DAY = 'day',
  WEEK = 'week',
  MONTH = 'month',
  QUARTER = 'quarter',
  YEAR = 'year',
  ISO_YEAR = 'iso-year',
  ISO_QUARTER = 'iso-quarter',
  ISO_WEEK = 'iso-week',
  ISO_WEEKDAY = 'iso-weekday',
}

/**
 * Describes the schema of a particular column.
 *
 * Not all attributes apply to all columns. These optional attributes are represented with nullable types.
 */
export interface IColumnSchema<T> {
  // The name of the column that should be rendered in UI.
  //
  // NOTE: Prefer [name] for programmatic references to columns.
  displayName?: string;

  // The underlying name of the column.
  name: string;

  // An optional Tableau Format String.
  format?: string;

  // The date granularity information for this column if we have any.
  dateGranularity?: DateGranularity;

  // The fiscal year start month for the column if any
  fiscalYearStartMonth?: number;

  // Indicates if this column holds timestamps
  // provided by the platform (instead of timestamps
  // that are provided by the underlying data)
  isSnapshotTimestamp?: boolean;

  // If present, indicates that the column is a table calculation
  tableCalculationInfo?: ITableCalculationInfo;
}

export interface ITableCalculationInfo {
  calculationDescription: string;
}

export interface IMetricDataPointsSchema {
  measure: IColumnSchema<number>;
  timestamp?: IColumnSchema<string>;
}

export interface IMetricDataPoints {
  points?: ReadonlyArray<IMetricDataPoint>;
  schema: IMetricDataPointsSchema;

  // ISO8601 string for when the data was last refreshed.
  // This is undefined if the data has never been refreshed.
  dataRefreshedTimestamp?: string;
}

export interface IMetricFailure {
  message: string;
  code: number;
}

export interface IMetricDataList {
  data: {[key: string]: IMetricDataPoints};
  failures: {[key: string]: IMetricFailure};
}
